import { CloseIconData, HelpIconData } from "@meiden-connect/material-symbols";
import { Button, Icon } from "@meiden-connect/ui";
import { GitLabTanukiIcon } from "@osakabe/auth-service-provider/shell";
import { useRef, useState } from "react";
import { loadTipsCollapsed, saveTipsCollapsed } from "../lib/tips-preference";
import {
  PROJECT_REPOSITORY_LINK_LABEL,
  PROJECT_REPOSITORY_LINK_TITLE,
  PROJECT_REPOSITORY_URL,
} from "../lib/project-links";
import { useCtrlWheelZoom } from "../features/canvas/input/use-ctrl-wheel-zoom";

// 操作 Tips パネル（画面左下固定）。
// デフォルトは折りたたみ表示（開閉ボタンと GitLab リポジトリリンクの 1 行だけ）、
// 開閉ボタンをクリックで展開してショートカット一覧を表示する。
// 全体マップは右下、操作ヒントは左下と画面の対角に分けて重なりを避ける。
// レイアウト（高さ上限・横幅上限・見た目）はすべて styles.css の .canvas-shortcut-tips 系クラス
// （根要素・コントロール行・一覧・リポジトリリンク）で与える。
// 展開時に上端が左上アプリメニューへ届いて覆ってしまうのを
// 防ぐため、上限は calc() / min() / max() と dvh フォールバックで表現する必要があり、
// インラインスタイルでは書けない。ここでインラインスタイルを復活させると CSS 側の上限を
// 上書きして無効化してしまうため、style 属性は付けない（単体テストで固定している）。
export function ShortcutTipsPanel(): JSX.Element {
  const [collapsed, setCollapsed] = useState<boolean>(() => loadTipsCollapsed());
  const toggle = (): void => {
    const next = !collapsed;
    setCollapsed(next);
    saveTipsCollapsed(next);
  };

  const toggleLabel = collapsed ? "操作ヒントを表示" : "操作ヒントを隠す";

  // このパネル上での Ctrl+ホイールもキャンバスズームに振り替える（ブラウザ拡大を防ぐ）。
  const rootRef = useRef<HTMLElement | null>(null);
  useCtrlWheelZoom(rootRef);

  return (
    <aside ref={rootRef} aria-label="操作ヒント" className="canvas-shortcut-tips">
      {/* 開閉ボタンとリポジトリリンクを 1 段の横並びへまとめる。
          親は column-reverse の縦積みなので、兄弟として並べると縦 2 段になり、
          一覧の高さ上限（--canvas-shortcut-tips-vertical-reserve）の見積もりも
          1 段ぶん足りなくなってアプリメニューへ重なる。 */}
      <div className="canvas-shortcut-tips-actions">
        {/* 作品の上に重なってもボタンだと分かる不透明な面・境界は、このクラスに styles.css が
            掛ける（隣のリポジトリリンクと同じ表面）。 */}
        <Button
          type="button"
          aria-expanded={!collapsed}
          aria-label={toggleLabel}
          title={toggleLabel}
          onClick={toggle}
          variant="action"
          className="canvas-shortcut-tips__toggle"
        >
          <Icon d={collapsed ? HelpIconData : CloseIconData} />
        </Button>
        {/* TOP フッターと同じアイコン・同じ遷移先・同じアクセシブル名にそろえる。
            編集中のキャンバスを離れさせないよう新しいタブで開く。
            @meiden-connect/ui の Button(to) は本リポジトリに使用実績が無く、
            別系統のルーターコンテキストを要求し得るため、素の <a> を使う
            （共通シェル AppFooter の既定も素の <a> である）。 */}
        <a
          className="canvas-shortcut-tips__repository-link"
          data-testid="canvas-repository-link"
          href={PROJECT_REPOSITORY_URL}
          rel="noreferrer noopener"
          target="_blank"
          title={PROJECT_REPOSITORY_LINK_TITLE}
        >
          <GitLabTanukiIcon className="canvas-shortcut-tips__gitlab-logo" />
          <span className="visually-hidden">{PROJECT_REPOSITORY_LINK_LABEL}</span>
        </a>
      </div>
      {!collapsed ? (
        // 上限高さを超える分はこの一覧の中でスクロールする。ポインタを使わない利用者でも
        // 後半の項目を読めるよう、Tab で到達できるようにする（PageUp / PageDown / Home /
        // End / Space でスクロールできる。矢印キーと W A S D はキャンバスのパンが優先される）。
        <ul className="canvas-shortcut-tips-list" tabIndex={0}>
          <li>ドラッグ または W/A/S/D キー: キャンバス移動</li>
          <li>Shift+W/A/S/D: 倍速で移動</li>
          <li>ホイール押し込みドラッグ: キャンバス移動（どのモードでも可）</li>
          <li>ホイール: 上下スクロール / Shift+ホイール: 左右（トラックパッドは 2 本指で縦横）</li>
          <li>
            Ctrl+ホイール: 拡大縮小（トラックパッドは 2 本指ピンチで無段階） / Ctrl+「+」「−」:
            段階ズーム
          </li>
          <li>G: グリッド吸着の切り替え / H: 左右ドラッグ反転（移動・範囲選択の入れ替え）</li>
          <li>
            F: 付箋 / N: セクション / R: 図形 / T: テキスト / M: ペン / L: 表 / E: スタンプ / C:
            コメント（もう一度押すと解除）
          </li>
          <li>Shift+ドラッグ（図形移動中）: x 軸 / y 軸を固定してまっすぐ移動</li>
          <li>Alt+ドラッグ（図形移動中）: 移動元を残して複製（ドロップ先にコピー）</li>
          <li>図形移動・複製中: 他の図形と端／中心が揃うと補助線が出て自動で整列</li>
          <li>Shift+ドラッグ（四隅ハンドル）: アスペクト比を保ったままリサイズ</li>
          <li>Shift+ドラッグ（ペン描画中）: 押した位置から現在位置まで直線を描画</li>
          <li>Ctrl+クリック: 図形を複数選択（移動・削除・コピーが可能）</li>
          <li>右クリック: 図形のロック / 最前面・最背面 / コピー / 削除メニュー</li>
          <li>Ctrl+C: 選択中の図形・テキスト・画像・リンクカードをコピー</li>
          <li>Ctrl+X: 選択中のオブジェクトを切り取り（コピーして削除）</li>
          <li>Ctrl+V: コピーしたオブジェクト / 画像 / URL / テキストをマウス位置へ貼り付け</li>
          <li>Delete / Backspace: 選択中のオブジェクトを削除</li>
          <li>F2: 選択中の付箋 / テキスト / 表の文字編集を開始（ダブルクリックと同じ）</li>
          <li>Tab / Shift+Tab（表のセル編集中）: 右 / 左のセルへ移動（最終セルで行を追加）</li>
          <li>Enter / Shift+Enter（表のセル編集中）: 下 / 上のセルへ移動（最終行で行を追加）</li>
          <li>Ctrl+B: 選択中のテキストを太字 / 通常へ切り替え</li>
          <li>Ctrl+B（本文編集中に文字を選択）: 選択した文字だけを太字 / 通常へ切り替え</li>
          <li>Ctrl+] / Ctrl+[: 選択中オブジェクトを前面 / 背面へ移動</li>
          <li>Ctrl+Z: 自分の直前操作を元に戻す</li>
          <li>Ctrl+Y: 元に戻した自分の操作をやり直す</li>
          {/* 固定版の解析器でも JSX の本文を読み取れるよう、文字参照を使う。 */}
          <li>ファイルをドラッグ&amp;ドロップで画像/動画を配置</li>
          <li>Esc: ツール解除</li>
        </ul>
      ) : null}
    </aside>
  );
}
